import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES, waGeneral } from "@/config/siteConfig";
import { useQuote } from "@/context/QuoteContext";
import { trackEvent } from "@/lib/analytics";
import EnquiryForm from "@/components/EnquiryForm";

const EASE = [0.22, 1, 0.36, 1];

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 1.0, ease: EASE },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: (delay) => ({ opacity: 1, y: 0, transition: { delay, duration: 0.8, ease: EASE } }),
};

const Hero = () => {
  const { openQuote } = useQuote();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Traditional houseboat on Dal Lake, Srinagar, Kashmir"
          className="h-full w-full object-cover"
          style={{ y: bgY, scale: 1.12 }}
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7 lg:pt-10">
            <motion.p
              custom={0.1} variants={fadeUp} initial="hidden" animate="visible"
              className="text-xs font-mono uppercase tracking-[0.25em] font-semibold text-[#D99B26] mb-5"
            >
              Kashmir Holiday Specialists
            </motion.p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1] text-balance">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" custom={0} variants={lineReveal} initial="hidden" animate="visible">
                  Your Kashmir Holiday,
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span className="block text-[#F3D9A0]" custom={1} variants={lineReveal} initial="hidden" animate="visible">
                  Beautifully Planned
                </motion.span>
              </span>
            </h1>
            <motion.p
              custom={0.65} variants={fadeUp} initial="hidden" animate="visible"
              className="mt-6 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed"
            >
              Discover Srinagar, Gulmarg, Pahalgam and more with personalised Kashmir holidays designed around your dates, preferences and budget.
            </motion.p>
            <motion.div
              custom={0.8} variants={fadeUp} initial="hidden" animate="visible"
              className="mt-8 flex flex-wrap gap-3"
            >
              <motion.span whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button onClick={() => openQuote()} data-testid="hero-get-quote-button"
                  className="bg-[#D99B26] text-[#0F2F24] hover:bg-[#c98f22] font-semibold rounded-full px-7 h-12 text-base shadow-lg">
                  Get My Kashmir Quote
                </Button>
              </motion.span>
              <motion.span whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button asChild variant="outline" data-testid="hero-whatsapp-button"
                  className="rounded-full px-7 h-12 text-base border-white/60 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm">
                  <a href={waGeneral()} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackEvent("whatsapp_click", { location: "hero" })}>
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp a Travel Expert
                  </a>
                </Button>
              </motion.span>
            </motion.div>
            <motion.p
              custom={0.95} variants={fadeUp} initial="hidden" animate="visible"
              className="mt-8 flex items-center gap-2 text-xs sm:text-sm font-medium text-white/70" data-testid="hero-trust-statement"
            >
              <ShieldCheck className="h-4 w-4 text-[#D99B26] shrink-0" />
              Hotels • Private Transfers • Sightseeing • Personalised Itineraries
            </motion.p>
          </div>

          <motion.div
            className="lg:col-span-5" data-testid="hero-quick-enquiry-form"
            initial={{ opacity: 0, y: 44 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9, ease: EASE }}
          >
            <div className="rounded-2xl bg-white p-6 sm:p-7 shadow-2xl ring-1 ring-black/5">
              <h2 className="font-serif text-2xl font-semibold text-[#0F2F24] mb-1">Plan Your Kashmir Holiday</h2>
              <p className="text-sm text-slate-600 mb-5">Free, personalised quote — no obligation.</p>
              <EnquiryForm source="home-hero" compact testIdPrefix="hero-enquiry" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
